import type { FunctionComponent, ReactNode } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { isEmbeddedDocument } from '@trello/browser';
import { intl } from '@trello/i18n';
import { MemberAvatar } from '@trello/member-avatar';
import { Button } from '@trello/nachos/button';
import type { InvitationLinkPageIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { PopUpWindow } from 'app/scripts/views/lib/PopUpWindow';
import { Facepile } from 'app/src/components/Facepile';
import { EmailRestrictionsList } from './EmailRestrictionsList';
import type { Member, Membership } from './InviteMembership.types';

import * as styles from './NotLoggedInUserText.module.less';

interface NotLoggedInUserTextProps {
  requiredDomain: string | null;
  emailRestrictions: string[];
  returnUrl: string;
  modelName: 'Board' | 'Team';
  analyticsContainers: {
    [key: string]: {
      id: string;
    };
  };
  fxQueueJoin?: () => void;
  memberString: ReactNode | string;
  memberships?: Membership[];
}

const emptyMemberships: Membership[] = [];
export const NotLoggedInUserText: FunctionComponent<
  NotLoggedInUserTextProps
> = ({
  requiredDomain,
  emailRestrictions,
  returnUrl,
  modelName,
  analyticsContainers,
  fxQueueJoin,
  memberString,
  memberships = emptyMemberships,
}) => {
  const sendRedirectButtonEvent = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (buttonName: any) => {
      const source =
        modelName === 'Board'
          ? 'acceptBoardInvitationScreen'
          : 'acceptTeamInvitationScreen';

      Analytics.sendClickedButtonEvent({
        buttonName,
        source,
        containers: analyticsContainers,
      });
    },
    [analyticsContainers, modelName],
  );

  const handleSignUpClick = useCallback(() => {
    sendRedirectButtonEvent('mainContentSignupButtonV2');

    fxQueueJoin?.();

    const suggestedEmail = requiredDomain
      ? intl.formatMessage(
          {
            id: 'templates.invitation_link.yourname-at-domain',
            defaultMessage: 'yourname@{domain}',
            description: 'Your name at domain',
          },
          { domain: requiredDomain },
        )
      : '';

    const params = new URLSearchParams({
      returnUrl: isEmbeddedDocument() ? '/embed/signup' : returnUrl,
      confirmReturnUrl: returnUrl,
      email: suggestedEmail,
    });
    const signUpUrl = '/signup?' + params.toString();

    if (isEmbeddedDocument()) {
      return new PopUpWindow(signUpUrl).open();
    } else {
      window.location.href = signUpUrl;
      return;
    }
  }, [fxQueueJoin, requiredDomain, returnUrl, sendRedirectButtonEvent]);

  const handleLoginClick = useCallback(() => {
    sendRedirectButtonEvent('mainContentLoginButtonV2');

    fxQueueJoin?.();

    const params = new URLSearchParams({
      returnUrl: isEmbeddedDocument() ? '/embed/login' : returnUrl,
    });
    const loginUrl = '/login?' + params.toString();

    if (isEmbeddedDocument()) {
      return new PopUpWindow(loginUrl).open();
    } else {
      window.location.href = loginUrl;
      return;
    }
  }, [fxQueueJoin, returnUrl, sendRedirectButtonEvent]);

  const memberIds = memberships.map((membership) => membership.member.id) || [];

  const renderAvatar = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (idMember: any) => {
      const memberData = memberships.find(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        (memberships) => memberships.member.id === idMember,
      )?.member as Member;

      return (
        <MemberAvatar
          idMember={idMember}
          memberData={memberData}
          size={40}
          testId={getTestId<InvitationLinkPageIds>(
            'invitation-link-page-member-avatar',
          )}
        />
      );
    },
    [memberships],
  );

  return (
    <>
      <div className={styles.facepileContainer}>
        <Facepile
          memberIds={memberIds}
          maxFaceCount={3}
          showMore={true}
          renderAvatar={renderAvatar}
          avatarSize={40}
        />
      </div>
      <p className="invitation-link-explanation">
        {modelName === 'Board' ? (
          memberString
        ) : (
          <FormattedMessage
            id="templates.invitation_link.before-you-can-join-you-ll-need-a-trello-account-team"
            defaultMessage="Looks like you need to be logged into your Trello account to join this Workspace."
            description="Message for logged out user trying to join a team"
          />
        )}
      </p>
      {requiredDomain ? (
        <p>
          <FormattedMessage
            id="templates.invitation_link.note-this-team-requires-you-log-in-with-an-email-from-domain-safe"
            defaultMessage="Note: This Workspace requires you sign up or log in using an email from {domain}."
            description="Message of required domain email for joining a team"
            values={{
              domain: <strong key="required-domain">{requiredDomain}</strong>,
            }}
          />
        </p>
      ) : (
        emailRestrictions.length > 0 && (
          <>
            <p>
              <FormattedMessage
                id="templates.invitation_link.note-this-team-requires-you-log-in-with-an-email-matching-one-of-the-following"
                defaultMessage="Note: This Workspace requires you sign up or log in using an email that looks like one of the following:"
                description="Message of required email for joining a team"
              />
            </p>
            <EmailRestrictionsList emailRestrictions={emailRestrictions} />
          </>
        )
      )}
      <div className="invitation-link-options">
        <Button
          className="nch-button nch-button--primary"
          onClick={handleSignUpClick}
          testId={getTestId<InvitationLinkPageIds>('signup-button')}
        >
          <FormattedMessage
            id="templates.invitation_link.sign-up"
            defaultMessage="Sign up"
            description="Sign up button"
          />
        </Button>
        <Button
          className="button"
          onClick={handleLoginClick}
          testId={getTestId<InvitationLinkPageIds>('login-button')}
        >
          <FormattedMessage
            id="templates.invitation_link.log-in"
            defaultMessage="Log in"
            description="Log in button"
          />
        </Button>
      </div>
      <div className="invitation-link-options-learn-more">
        <a className="quiet" href="/tour" target="tour">
          <FormattedMessage
            id="templates.invitation_link.learn-more-about-trello-simple"
            defaultMessage="Learn more about Trello"
            description="Learn more about Trello link"
          />
        </a>
      </div>
    </>
  );
};
